//Get session related data from the server
import {
    IClientSessionState,
    ISessionContext,
    ISessionContextLoadOptions,
    ISAPTokenRequest,
    ISAPTokenResponse
} from "./PA.Core.IF.Session";
import {PAEnvironment} from "./PAEnvironment";
import axios from "axios";
import * as _ from "lodash";
import {PAdbContext} from "./PAdbContext";
import {WB_SessionRow} from "./PAdbSchemaRows";
import {ProgressProvider} from "./Basic/Progress";
import {ASync} from "./Basic/ASync";
import {IProgressProvider} from "./PA.Core.IF.Basic";
import {esWB_Session, esWB_User} from "./PAdbSchema";
import {PAusrContext, PAusrContextCreateOptions} from "./PAusrContext";
import {IUsrContextLoadOptions} from "./PA.Core.IF.User";


export class PASessionContext
    implements ISessionContext {

    private _sessionGUID: string;
    private _session: WB_SessionRow;
    private _usrContext: PAusrContext;
    private _dbContext: PAdbContext
    private _createOpts: PAusrContextCreateOptions
    private _bIsLoaded: boolean;
    private _OnLoadComplete: Promise<PASessionContext>
    private _LoadProgressProvider: ProgressProvider

    private _bIsValid: boolean
    private _iTimeoutPeriodSeconds = 60 * 15

    constructor(options?: PAusrContextCreateOptions) {
        let opts: PAusrContextCreateOptions = options || {}
        this._createOpts = opts
        this._sessionGUID = "na"
        this._session = undefined
        this._dbContext = new PAdbContext({})
        this._usrContext = new PAusrContext()
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this._LoadProgressProvider = new ProgressProvider()

        if ('iTimeoutPeriodSeconds' in opts) {
            this._iTimeoutPeriodSeconds = opts.iTimeoutPeriodSeconds
        }

        this._bIsValid = false
    }


    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    get OnLoadComplete(): Promise<ISessionContext> {
        if (this.bIsLoaded) {
            return ASync.resolve(this)
        }

        if (!_.isEmpty(this._OnLoadComplete)) {
            return this._OnLoadComplete
        }

        return ASync.error("Load not initiated or failed.")
    }

    get UserContext(): PAusrContext {
        return this._usrContext
    }

    get dbContext(): PAdbContext {
        return this._dbContext
    }

    get Session_GUID(): string {
        return this._sessionGUID
    }

    set Session_GUID(value:string){
        this._sessionGUID=value
    }

    get Session(): WB_SessionRow {
        return this._session
    }

    get LoadProgressProvider(): IProgressProvider {
        return this._LoadProgressProvider
    }

    Load(options: ISessionContextLoadOptions): Promise<ISessionContext> {
        const self = this;

        const opts: ISessionContextLoadOptions = <any>(options || {})

        self.LoadProgressProvider.start("Loading Session");

        let rv = self._OnLoadComplete = (async () => {

            try {
                let sessState = await self.getSessionState()

                if (_.isEmpty(sessState)) {
                    self.LoadProgressProvider.exception("Session State not found: " + sessState)
                    document.cookie = "PA-REST-GUID=;expires=Thu, 01 Jan 1970 00:00:01 GMT;";
                    return self
                }

                if (!_.isEmpty(opts.GuidOverride)) {
                    self._sessionGUID = opts.GuidOverride
                } else {
                    self._sessionGUID = sessState.session_guid
                }
                self._iTimeoutPeriodSeconds = sessState.session_client_timeout

                if (_.isEmpty(self._sessionGUID)) {
                    self.LoadProgressProvider.exception("Session GUID not found: " + sessState)
                    return self
                }

                PAEnvironment.setValue(PAEnvironment.ESCustomColumns, sessState.custom_columns)
                PAEnvironment.setValue(PAEnvironment.PostLoginURL, sessState.PostLoginURL)
                PAEnvironment.setValue(PAEnvironment.LeaseServerURL, sessState.lease_server_url)

                let arrWB_Session_Rows: WB_SessionRow[] = await self.dbContext.findAll(esWB_Session, {where: {WB_SESSION_GUID: self._sessionGUID, DELETED_IND: {neq: '1'}}}, {with: [esWB_User]})

                if (_.isEmpty(arrWB_Session_Rows)) {
                    self.LoadProgressProvider.exception("Session not found: " + self._sessionGUID)
                    return self
                }
                self._session = arrWB_Session_Rows[0]
                options.iTimeoutPeriodSeconds = self._iTimeoutPeriodSeconds
                let usrCtxOptions: IUsrContextLoadOptions = {
                    wbAuthUsrId: self._session.cif.WB_AUTHUSR_ID,
                    iTimeoutPeriodSeconds: options.iTimeoutPeriodSeconds
                }
                await self._usrContext.Load(usrCtxOptions)
                await self._usrContext.OnLoadComplete
                PAEnvironment.setValue(PAEnvironment.UserContext, self._usrContext);
                await self.getSAPToken()

                this._bIsLoaded = true
                self.LoadProgressProvider.finish()
                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                // Load the Login Page
                PAEnvironment.setValue("Login.Error", err);
                console.log(err);
                return self
            }

        })();

        return rv
    }


    private async getSessionState(): Promise<IClientSessionState> {
        const self = this

        try {
            let sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/"));

            if (sEndPoint.endsWith('/'))
                sEndPoint += 'PASession';
            else
                sEndPoint += '/PASession';

            let resp = await axios.post(sEndPoint, {createOptions: self._createOpts, bNode: PAEnvironment.isNode()})

            let responseData: IClientSessionState = resp.data;
            if (!_.isEmpty(responseData))
                return responseData;
            else
                return <any> {}

        } catch (err: any) {
            throw 'Unable to obtain Session data from Server. ' + err
        }

    }

    private getSAPToken(): Promise<void> {

        return new Promise<void>((resolve, reject) => {
            if (_.isEmpty(this._usrContext.User)) return

            let sEndPoint = <string>(PAEnvironment.getValue(PAEnvironment.Network_URL_Base, "/"));
            if (sEndPoint.endsWith('/'))
                sEndPoint += 'PASAPToken';
            else
                sEndPoint += '/PASAPToken';
            let aRequest: ISAPTokenRequest = { userId: this._usrContext.User.cif.AUTHUSR_ID}

            axios.get(sEndPoint, {
                params:aRequest
            })
                .then((resp: any) => {
                    let responseData: ISAPTokenResponse = resp.data;
                    if (!_.isEmpty(responseData)) {
                        PAEnvironment.setValue(PAEnvironment.SAPToken, responseData.token)
                        resolve();
                    } else {
                        reject('Unable to parse Token Web Response: ' + responseData);
                    }
                })
                .catch((err: any) => {
                    reject('Unable to obtain token from Server. ' + err);
                });
        });
    }

}
